import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { paths, images } from '../data/village-data';
import ShareButtons from './ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PathPage() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const path = paths.find((p) => p.id === pathId);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!path) {
    return (
      <section className="path-page path-page--not-found">
        <div className="path-content">
          <h1>Path not found</h1>
          <button className="village-btn village-btn--primary" onClick={() => navigate('/life-map')}>
            Return to Life-Map
          </button>
        </div>
      </section>
    );
  }

  // Pick a background image based on path
  const bgMap = {
    'fuck-thatt': images.treeOfLife,    // was lectureHall — now uses Tree of Life with a fire-overlay treatment
    'life-shopping': images.treeOfLife,
    'gangstachef': images.kitchen,
    'fear': images.treeOfLife,
    'me-treat': images.meditation,
    'fire-circle': images.treeOfLife,
  };
  const bgImage = bgMap[path.id] || images.treeOfLife;

  // Path-specific FX (mood treatment per path)
  const renderPathFX = () => {
    if (path.id === 'fuck-thatt') {
      // Awakening fire — red/gold blaze + rising embers
      return (
        <div className="path-fx path-fx--fire" aria-hidden="true">
          <div className="path-fx-glow" />
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className={`path-ember path-ember--${i + 1}`} />
          ))}
        </div>
      );
    }
    if (path.id === 'fear') {
      // Face Everything And Rise — dawn light rays rising from bottom
      return (
        <div className="path-fx path-fx--dawn" aria-hidden="true">
          <div className="path-fx-dawn-glow" />
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className={`path-ray path-ray--${i + 1}`} />
          ))}
        </div>
      );
    }
    if (path.id === 'fire-circle') {
      // Intimate firelight — concentrated warm glow + gentle flickering
      return (
        <div className="path-fx path-fx--circle" aria-hidden="true">
          <div className="path-fx-circle-glow" />
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className={`path-spark path-spark--${i + 1}`} />
          ))}
        </div>
      );
    }
    if (path.id === 'me-treat') {
      // Moonlit stillness — cool silver-blue breathing glow + floating motes
      return (
        <div className="path-fx path-fx--moon" aria-hidden="true">
          <div className="path-fx-moon-glow" />
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className={`path-mote path-mote--${i + 1}`} />
          ))}
        </div>
      );
    }
    if (path.id === 'life-shopping') {
      // Lantern-lit path — warm scattered lantern glows
      return (
        <div className="path-fx path-fx--lanterns" aria-hidden="true">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className={`path-lantern path-lantern--${i + 1}`} />
          ))}
        </div>
      );
    }
    if (path.id === 'gangstachef') {
      // Kitchen warmth — rising golden steam
      return (
        <div className="path-fx path-fx--steam" aria-hidden="true">
          <div className="path-fx-steam-glow" />
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className={`path-steam path-steam--${i + 1}`} />
          ))}
        </div>
      );
    }
    return null;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const endpoint = path.id === 'me-treat' ? '/api/metreat-interest' : '/api/email-capture';
      await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      // still show success to not block user
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={`path-page-${path.id}`}
      className={`path-page path-page--${path.id}`}
      style={{
        backgroundImage: `linear-gradient(rgba(15,10,25,0.65), rgba(15,10,25,0.8)), url(${bgImage})`,
      }}
    >
      {renderPathFX()}
      {/* Back navigation */}
      <nav className="path-nav" data-testid="path-nav">
        <Link to="/life-map" className="path-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Life-Map
        </Link>
      </nav>

      <div className="path-content">
        <div className="path-hero">
          <h1 data-testid="path-title" className="path-title" style={{ color: path.color }}>
            {path.name}
          </h1>
          {path.subtitle && (
            <p className="path-subtitle">{path.subtitle}</p>
          )}
          <div className="path-divider" style={{ backgroundColor: path.color }} />
          <p className="path-message">
            {path.message.split('\n').map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
          </p>
          {path.purpose && (
            <p className="path-purpose">{path.purpose}</p>
          )}
          {path.quote && (
            <blockquote className="path-quote" data-testid="path-quote">
              {path.quote.split('\n').map((line, i) => (
                <span key={i}>{line}<br /></span>
              ))}
            </blockquote>
          )}
        </div>

        {/* Placeholder sections */}
        <div className="path-placeholders">
          <h2 className="path-section-heading">Coming Soon</h2>
          <div className="placeholder-grid">
            {path.placeholders.map((item, i) => (
              <div key={i} data-testid={`placeholder-${i}`} className="placeholder-card">
                <div className="placeholder-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h3 className="placeholder-title">{item}</h3>
                <p className="placeholder-text">Content is being crafted for this experience.</p>
              </div>
            ))}
          </div>
        </div>

        {/* Email capture */}
        <div className="path-email-section">
          <h2 className="path-email-heading">Stay Connected</h2>
          <p className="path-email-text">
            Be the first to know when this path opens. Join the Village Manifestors.
          </p>
          {submitted ? (
            <p data-testid="email-success" className="path-email-success">
              You said yes to yourself. The Village remembers.
            </p>
          ) : (
            <form onSubmit={handleEmailSubmit} className="path-email-form">
              <input
                type="email"
                data-testid="path-email-input"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="path-email-input"
              />
              <button
                type="submit"
                data-testid="path-email-submit"
                className="village-btn village-btn--primary"
                disabled={submitting}
              >
                {submitting ? 'Joining...' : 'Join the Path'}
              </button>
            </form>
          )}
        </div>

        {/* Share */}
        <ShareButtons
          url={`https://thealchemyvillage.com/path/${path.id}`}
          title={`${path.name} — The Alchemy Village`}
          text={path.subtitle || 'A path of transformation inside The Alchemy Village.'}
          image={bgImage}
          variant="gold"
        />

        {/* Navigation to other paths */}
        <div className="path-explore">
          <h2 className="path-explore-heading">Explore Other Paths</h2>
          <div className="path-explore-links">
            {paths
              .filter((p) => p.id !== path.id)
              .slice(0, 3)
              .map((p) => (
                <Link
                  key={p.id}
                  to={p.route}
                  data-testid={`explore-${p.id}`}
                  className="path-explore-link"
                  style={{ '--path-color': p.color }}
                >
                  <span className="explore-link-name">{p.name}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
